import { useQuery } from "@tanstack/react-query";
import {
  ENGLISH_LANG_CODE,
  IDefaultEmailTemplate,
  IEmailTemplate,
  TypeEmailTemplates,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { RequestKey } from "api/request-key";

type Args = {
  type: TypeEmailTemplates;
  showId?: string;
  languageCode: string;
  isDefault?: boolean;
  enabled?: boolean;
};

export type UseLocalisedEmailTemplateRes = {
  emailTemplate: IDefaultEmailTemplate | IEmailTemplate | null;
  isLoading: boolean;
  isMissing: boolean;
};

export const useLocalisedEmailTemplate = ({
  type,
  showId,
  languageCode,
  isDefault = false,
  enabled = true,
}: Args): UseLocalisedEmailTemplateRes => {
  const isEnglish = !languageCode || languageCode === ENGLISH_LANG_CODE;

  const { data, isFetching, isError } = useQuery({
    queryKey: [
      RequestKey.GetLocalisedEmailTemplateApiV2,
      showId,
      type,
      languageCode,
      isDefault,
    ],
    queryFn: (): Promise<IDefaultEmailTemplate | IEmailTemplate> =>
      isDefault
        ? apiV2.emailTemplate.default.getOne({ type, languageCode })
        : apiV2.emailTemplate.getOne({
            type,
            showId: showId as string,
            languageCode,
          }),
    enabled: enabled && !isEnglish && !!showId && !!type,
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return {
    emailTemplate: isEnglish ? null : data ?? null,
    isLoading: !isEnglish && isFetching,
    isMissing: !isEnglish && isError,
  };
};

export default useLocalisedEmailTemplate;
